import { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Collapse from '@mui/material/Collapse';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import LoadingButton from '@mui/lab/LoadingButton';
import Typography from '@mui/material/Typography';
import { Quote } from '../../../../../../Typescript/backend/classes';
import { useUsingVagoSettings } from '../../../../../../hooks';
import { useAuth } from '@calefy-inc/authentication';
import { useLazyQuery, useMutation } from '@apollo/client';
import { graphql } from '../../../../../../gql';
import { GetVagoQuoteClaimsQuery } from '../../../../../../gql/graphql';
import { ArrayElement } from '@calefy-inc/utilityTypes';
import { styled } from '@mui/material';
import LoadingScreen from '../../../LoadingScreen';
import {
  SOME_FINAL_FORMS,
  BUSINESS_TYPE,
  POLICY,
} from '../../../../../../queries';
import {
  VAGO_BUSINESS_INTERNAL_NAME,
  VAGO_POLICY_INTERNAL_NAMES,
} from '../../../../../../WhiteLabelSettings/VagoAssure/constants';
import { ProgramBuilderForm } from '../../../../../FormManager/classes';
import { Language } from '../../../../../../Typescript/classes';
import {
  QuoteWizardForm,
  QuoteWizardQuestionInstance,
} from '../../../../../QuoteWizard/classes';
import { Form } from 'informed';
import RenderedQuestionInstance from '../../../../../common/QuoteComponents/RenderedQuestionInstance';
import {
  WithFormStyles,
  withFormStyles,
} from '../../../../../../util/withFormStyles';
import { formStateToAnswerInstances } from '../../../../../QuoteWizard/QuoteForms/utility';
import { AnswerInstance } from '../../../../../../Typescript/ModelTypes/AnswerInstance';

const GET_VAGO_QUOTE_CLAIMS = graphql(`
  query getVagoQuoteClaims($token: String!, $quoteUuid: String!) {
    getVagoQuoteClaims(token: $token, quoteUuid: $quoteUuid) {
      type
      value
      effectiveDate
      dateReported
      address
      answer {
        id
        apiName
      }
      baseAnswer {
        id
        apiName
      }
    }
  }
`);

const ADD_VAGO_CLAIM = graphql(`
  mutation addVagoClaim(
    $token: String!
    $answerInstanceInput: AnswerInstanceInput!
    $parentAnswerId: ID
    $type: String
    $quoteUuid: String!
  ) {
    addVagoClaim(
      token: $token
      answerInstanceInput: $answerInstanceInput
      parentAnswerId: $parentAnswerId
      type: $type
      quoteUuid: $quoteUuid
    ) {
      answer {
        id
        apiName
      }
    }
  }
`);

type VagoClaim = ArrayElement<
  NonNullable<GetVagoQuoteClaimsQuery['getVagoQuoteClaims']>
>;
type VagoClaimAnswer = VagoClaim['baseAnswer'];

interface VagoClaimsButtonProps {
  quote: Quote | null;
}

export const VagoClaimsButton = ({ quote }: VagoClaimsButtonProps) => {
  const onVago = useUsingVagoSettings();
  const [open, setOpen] = useState<boolean>(false);

  if (!onVago) {
    return null;
  }

  if (!quote) {
    return null;
  }

  return (
    <>
      <Button onClick={() => setOpen(true)}>Claims</Button>
      <VagoClaimsDialog
        quote={quote}
        open={open}
        setOpen={(open) => setOpen(open)}
      />
    </>
  );
};

interface VagoClaimsDialogProps {
  quote: Quote;
  open: boolean;
  setOpen: (open: boolean) => void;
}

const VagoClaimsDialog = ({ quote, open, setOpen }: VagoClaimsDialogProps) => {
  const { token } = useAuth();
  const [getVagoQuoteClaims, { data, error, loading }] = useLazyQuery(
    GET_VAGO_QUOTE_CLAIMS,
  );
  const [categorizedClaims, setCategorizedClaims] = useState<
    Record<string, { claims: VagoClaim[]; answer: AnswerInstance }>
  >({}); // ones belonging to a specific risk address
  const [uncategorizedClaims, setUncategorizedClaims] = useState<
    Array<VagoClaim>
  >([]);

  // Fetch claims when token is present
  useEffect(() => {
    if (token && quote?.uniqueId && open) {
      getVagoQuoteClaims({
        variables: {
          token,
          quoteUuid: quote.uniqueId,
        },
      });
    }
  }, [token, quote?.uniqueId, open]);

  // Categorize claims by base answer
  useEffect(() => {
    if (data?.getVagoQuoteClaims) {
      type ReduceAccumulator = {
        categorized: Record<
          string,
          { claims: Array<VagoClaim>; answer: AnswerInstance }
        >;
        uncategorized: Array<VagoClaim>;
      };

      // @ts-ignore - types will be generated
      const { categorized, uncategorized } = data.getVagoQuoteClaims.reduce(
        (acc: ReduceAccumulator, claim: VagoClaim) => {
          if (!claim) {
            return acc;
          }
          const baseAnswer = claim?.baseAnswer;
          const isRiskAddress = baseAnswer?.apiName === 'risk_address';

          if (isRiskAddress && baseAnswer?.id) {
            if (!acc.categorized[baseAnswer.id]) {
              acc.categorized[baseAnswer.id] = {
                claims: [],
                answer: baseAnswer,
              };
            }
            acc.categorized[baseAnswer.id].claims.push(claim);
          } else {
            acc.uncategorized.push(claim);
          }
          return acc;
        },
        { categorized: {}, uncategorized: [] },
      );

      // now ensure that all risk_address answers have some claims information associated with them, just in case we need to add them later
      const propertyForms = quote.completedForms.filter(
        (form) => form.policy?.internalName === 'property',
      );
      propertyForms.forEach((form) => {
        const riskAddresses = form.answers.filter(
          (answer) => answer.apiName === 'risk_address',
        );
        riskAddresses.forEach((addressAnswer) => {
          // @ts-expect-error
          if (!categorized[addressAnswer.id]) {
            //console.log('Adding', addressAnswer, 'to categorized');
            // @ts-expect-error
            categorized[addressAnswer.id] = {
              claims: [],
              answer: addressAnswer,
            };
          }
        });
      });
      setCategorizedClaims(categorized);
      setUncategorizedClaims(uncategorized);
    }
  }, [data]);
  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth='md' fullWidth>
      <DialogTitle>Claims</DialogTitle>
      <DialogContent>
        <Collapse in={loading}>
          <LoadingScreen />
        </Collapse>
        <DisplayClaimsError error={error} />
        <Collapse in={!loading}>
          <ClaimSection>
            {Object.entries(categorizedClaims).map(
              ([baseAnswerId, { claims, answer }]) => (
                <DisplayAnswerClaims
                  key={baseAnswerId}
                  topLevelAnswer={answer}
                  claims={claims}
                  quoteUuid={quote.uniqueId}
                  type='risk address'
                />
              ),
            )}
          </ClaimSection>
        </Collapse>

        <ClaimSection>
          {/* We want this to be displayed whether or not there are existing claims */}
          <Collapse in={!loading}>
            <DisplayAnswerClaims
              topLevelAnswer={uncategorizedClaims[0]?.baseAnswer || null}
              claims={uncategorizedClaims}
              quoteUuid={quote.uniqueId}
              type='uncategorized'
            />
          </Collapse>
        </ClaimSection>
      </DialogContent>
    </Dialog>
  );
};

const ClaimSection = styled('section')(() => {
  return {};
});

interface DisplayClaimsErrorProps {
  error?: Error;
}
const DisplayClaimsError = ({ error }: DisplayClaimsErrorProps) => {
  return (
    <Collapse in={!!error}>
      <Alert severity='error'>
        Error when getting claims. {error ? error.message : null}
      </Alert>
    </Collapse>
  );
};

interface DisplayAnswerClaimsProps {
  topLevelAnswer: VagoClaimAnswer | null;
  claims: Array<VagoClaim>;
  quoteUuid: string;
  type: 'risk address' | 'uncategorized';
}

const DisplayAnswerClaims = ({
  topLevelAnswer,
  claims,
  quoteUuid,
  type,
}: DisplayAnswerClaimsProps) => {
  const [title, setTitle] = useState<string>('');
  const [isRiskAddress, setIsRiskAddress] = useState<boolean>(false);

  // set the title once we can
  useEffect(() => {
    if (isRiskAddress) {
      let address: string = 'Unknown';
      if (claims.length > 0) {
        const addressClaim = claims.find((claim) => claim.address);
        address = addressClaim ? addressClaim.address : 'Unknown';
      } else if (topLevelAnswer) {
        address = ['address', 'city', 'postal', 'province']
          .map((apiName) =>
            topLevelAnswer.findValueByAttributeInSubanswers('apiName', apiName),
          )
          .filter((elem) => !!elem)
          .join(', ');
      }
      setTitle(`Risk Address: ${address}`);
    } else {
      setTitle('Claims Not Associated with an Insured Location');
    }
  }, [claims, topLevelAnswer, isRiskAddress]);

  // is this a risk address answer?
  useEffect(() => {
    setIsRiskAddress(type === 'risk address');
  }, [type]);

  return (
    <ClaimAnswerContainer>
      <h3>{title}</h3>
      {claims.length === 0 ? (
        <NoClaimsComponent />
      ) : (
        claims.map((claim, index) => (
          <DisplayClaim
            key={index}
            claim={claim}
            displayAddress={!isRiskAddress}
          />
        ))
      )}
      <AddClaimButton
        baseAnswer={topLevelAnswer}
        sampleClaim={claims[0] || null}
        quoteUuid={quoteUuid}
        type={type}
      />
    </ClaimAnswerContainer>
  );
};

const NoClaimsComponent = () => {
  return <Typography>No claims present</Typography>;
};

interface AddClaimButtonProps {
  baseAnswer: VagoClaimAnswer | null;
  sampleClaim: VagoClaim | null;
  quoteUuid: string;
  type: DisplayAnswerClaimsProps['type'];
}
const AddClaimButton = ({
  baseAnswer,
  sampleClaim,
  quoteUuid,
  type,
}: AddClaimButtonProps) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        startIcon={<AddIcon />}
        onClick={() => setOpen(true)}
        variant='outlined'
        size='small'
      >
        Add Claim
      </Button>
      <AddClaimDialog
        open={open}
        setOpen={setOpen}
        baseAnswer={baseAnswer}
        sampleClaim={sampleClaim}
        quoteUuid={quoteUuid}
        type={type}
      />
    </>
  );
};

interface AddClaimDialogProps extends WithFormStyles {
  open: boolean;
  setOpen: (open: boolean) => void;
  baseAnswer: VagoClaimAnswer | null;
  sampleClaim: VagoClaim | null;
  quoteUuid: string;
  type: AddClaimButtonProps['type'];
}

const AddClaimDialog = withFormStyles()(
  ({
    open,
    setOpen,
    baseAnswer,
    sampleClaim,
    type,
    classes,
    quoteUuid,
  }: AddClaimDialogProps) => {
    const { token } = useAuth();
    const [quoteWizardForm, setQuoteWizardForm] =
      useState<QuoteWizardForm | null>(null);
    const [questionInstance, setQuestionInstance] =
      useState<QuoteWizardQuestionInstance | null>(null);

    // Queries to get business and policy IDs
    const [
      getBusiness,
      { data: businessData, loading: businessLoading, error: businessError },
    ] = useLazyQuery(BUSINESS_TYPE);

    const [
      getPolicy,
      { data: policyData, loading: policyLoading, error: policyError },
    ] = useLazyQuery(POLICY);

    // Query for forms (works for both general info and property)
    const [
      getForms,
      { data: formsData, loading: formsLoading, error: formsError },
    ] = useLazyQuery(SOME_FINAL_FORMS);

    // Mutation for adding a claim
    const [
      addVagoClaim,
      {
        loading: submitLoading,
        error: submitError,
        data: submitData,
        reset: resetMutation,
      },
    ] = useMutation(ADD_VAGO_CLAIM, {
      refetchQueries: ['getVagoQuoteClaims', 'Quote'],
    });

    // Fetch business and policy IDs when dialog opens
    useEffect(() => {
      if (open) {
        getBusiness({
          variables: {
            internalName: VAGO_BUSINESS_INTERNAL_NAME,
          },
        });

        if (type === 'risk address') {
          getPolicy({
            variables: {
              internalName: VAGO_POLICY_INTERNAL_NAMES[0], // 'property'
            },
          });
        }
      }
    }, [open, type]);

    // Fetch the appropriate form once we have the IDs
    useEffect(() => {
      const businessId = businessData?.businessType?.id;

      if (businessId) {
        if (type === 'risk address') {
          const policyId = policyData?.policy?.id;
          if (policyId) {
            // Get property form (with policyId)
            getForms({
              variables: {
                businessId,
                policyId,
              },
            });
          }
        } else {
          // Get general information form (no policyId)
          getForms({
            variables: {
              businessId,
            },
          });
        }
      }
    }, [businessData, policyData, type]);

    // Convert backend form data to QuoteWizardForm
    useEffect(() => {
      if (formsData?.someFinalForms && formsData.someFinalForms.length > 0) {
        const backendForm = formsData.someFinalForms.find((form) =>
          type === 'risk address'
            ? form?.policy?.internalName === 'property'
            : form?.policy === null,
        );
        //console.log({ backendForm });
        const convertedForm = ProgramBuilderForm.generateFromBackendResponse(
          backendForm,
        ).toQuoteWizardForm(Language.english);
        //console.log({ convertedForm });
        setQuoteWizardForm(convertedForm);
      }
    }, [formsData, type]);

    // Extract question instance with matching apiName from claim's answer
    useEffect(() => {
      if (quoteWizardForm) {
        const found =
          quoteWizardForm.findQuestionByApiName('claims_information');

        if (found) {
          // Modify the question to make repeatable: false in propsBlob
          const modifiedQuestion = new QuoteWizardQuestionInstance({
            ...found,
            propsBlob: {
              ...found.propsBlob,
              repeatable: false,
            },
          });
          setQuestionInstance(modifiedQuestion);
        }
      }
    }, [quoteWizardForm, sampleClaim]);

    // Reset form state when dialog opens/closes
    useEffect(() => {
      if (open) {
        resetMutation();
      }
    }, [open, resetMutation]);

    // Auto-close dialog after 5 seconds on success
    useEffect(() => {
      if (submitData) {
        const timer = setTimeout(() => {
          setOpen(false);
        }, 5_000);
        return () => clearTimeout(timer);
      }
    }, [submitData, setOpen]);

    // Handle form submission
    const loading = businessLoading || policyLoading || formsLoading;
    const error = businessError || policyError || formsError || submitError;
    return (
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        maxWidth='lg'
        fullWidth
      >
        <DialogTitle>
          Add Claim -{' '}
          {type === 'risk address'
            ? 'Property Form'
            : 'General Information Form'}
        </DialogTitle>
        <DialogContent>
          <Collapse in={loading}>
            <LoadingScreen />
          </Collapse>
          <DisplayFormLoadError error={error} />
          <ClaimSuccessComponent success={!!submitData} />
          {questionInstance ? (
            <Form
              className={classes.form}
              onSubmit={(formState) => {
                const { values } = formState;
                //console.log('In onsubmit with', values);
                // Convert form state to answer instances
                const answerInstances = formStateToAnswerInstances(values, [
                  questionInstance,
                ]);

                if (answerInstances.length === 0) {
                  //console.log('No answer instances; aborting');
                  return;
                }

                // Convert to UnifiedAnswerInstance
                const unifiedAnswers = answerInstances.map((answerInstance) =>
                  answerInstance.toUnifiedAnswerInstance(),
                );

                // Convert to AnswerInstanceInput
                const answerInstanceInputs = unifiedAnswers.map(
                  (unifiedAnswer) => unifiedAnswer.toAnswerInstanceInput(),
                );

                addVagoClaim({
                  variables: {
                    token,
                    answerInstanceInput: answerInstanceInputs[0],
                    parentAnswerId: baseAnswer?.id || undefined,
                    quoteUuid,
                    type,
                  },
                });
              }}
            >
              <StyledFieldset disabled={submitLoading}>
                <RenderedQuestionInstance
                  questionInstance={questionInstance}
                  classes={classes}
                />
              </StyledFieldset>
              <Collapse in={!!submitError}>
                <Alert severity='error' sx={{ mb: 2 }}>
                  Error submitting claim: {submitError?.message}
                </Alert>
              </Collapse>
              <ClaimSuccessComponent success={!!submitData} />
              <DialogActions>
                <Button onClick={() => setOpen(false)} color='inherit'>
                  Cancel
                </Button>
                <LoadingButton
                  loading={submitLoading}
                  type='submit'
                  variant='contained'
                  color='primary'
                >
                  {submitLoading ? 'Submitting...' : 'Submit'}
                </LoadingButton>
              </DialogActions>
            </Form>
          ) : null}
        </DialogContent>
      </Dialog>
    );
  },
);

interface ClaimSuccessComponentProps {
  success: boolean;
}
const ClaimSuccessComponent = ({ success }: ClaimSuccessComponentProps) => {
  return (
    <Collapse in={success}>
      <Alert severity='success' sx={{ mb: 2 }}>
        Claim added successfully! This dialog will close shortly.
      </Alert>
    </Collapse>
  );
};

const StyledFieldset = styled('fieldset')({
  border: 'none',
});

interface DisplayFormLoadErrorProps {
  error?: Error;
}
const DisplayFormLoadError = ({ error }: DisplayFormLoadErrorProps) => {
  return (
    <Collapse in={!!error}>
      <Alert severity='error'>
        Error with claims update process. {error ? error.message : null}
      </Alert>
    </Collapse>
  );
};

interface DisplayClaimProps {
  claim: VagoClaim;
  displayAddress: boolean;
}
const DisplayClaim = ({ claim, displayAddress }: DisplayClaimProps) => {
  return (
    <ClaimCard>
      {displayAddress && claim.address ? (
        <ClaimRow>
          <ClaimLabel>Address:</ClaimLabel>
          <ClaimValue>{claim.address}</ClaimValue>
        </ClaimRow>
      ) : null}
      <ClaimRow>
        <ClaimLabel>Type:</ClaimLabel>
        <ClaimValue>{claim.type || 'N/A'}</ClaimValue>
      </ClaimRow>

      {claim.value !== null && claim.value !== undefined ? (
        <ClaimRow>
          <ClaimLabel>Value:</ClaimLabel>
          <ClaimValue>${claim.value.toLocaleString()}</ClaimValue>
        </ClaimRow>
      ) : null}

      {claim.effectiveDate ? (
        <ClaimRow>
          <ClaimLabel>Effective Date:</ClaimLabel>
          <ClaimValue>
            {new Date(claim.effectiveDate).toLocaleDateString()}
          </ClaimValue>
        </ClaimRow>
      ) : null}

      {claim.dateReported ? (
        <ClaimRow>
          <ClaimLabel>Date Reported:</ClaimLabel>
          <ClaimValue>
            {new Date(claim.dateReported).toLocaleDateString()}
          </ClaimValue>
        </ClaimRow>
      ) : null}
    </ClaimCard>
  );
};

const ClaimAnswerContainer = styled('div')(({ theme }) => {
  return {
    marginBottom: theme.spacing(6),
    '& h3': {
      ...theme.typography.h6,
      marginBottom: theme.spacing(2),
      color: theme.palette.text.primary,
      fontWeight: theme.typography.fontWeightMedium,
    },
  };
});

const ClaimCard = styled('div')(({ theme }) => {
  return {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    boxShadow: theme.shadows[1],
    transition: theme.transitions.create(['box-shadow', 'border-color'], {
      duration: theme.transitions.duration.short,
    }),
    '&:hover': {
      boxShadow: theme.shadows[3],
      borderColor: theme.palette.primary.main,
    },
  };
});

const ClaimRow = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0,
    },
  };
});

const ClaimLabel = styled('span')(({ theme }) => {
  return {
    ...theme.typography.body2,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.secondary,
    minWidth: '140px',
    marginRight: theme.spacing(2),
  };
});

const ClaimValue = styled('span')(({ theme }) => {
  return {
    ...theme.typography.body2,
    color: theme.palette.text.primary,
    flex: 1,
    wordBreak: 'break-word',
  };
});
